// O124 — Selection Tuning ("What-if") sandbox, a tab on the Compliance Selections page.
//
// Pick a batch (or a device + date range), drag any selection threshold, and see every one of
// that batch's REAL photos re-verdict live: which frame would be selected, which would be held,
// and why — computed by the SAME engine production runs (POST /picture/compliance/simulate),
// against each frame's already-recorded metrics. Nothing is written; this is a testing surface.
// "Apply to production" is deliberately absent — the live Threshold Bench remains that path.
//
// The controls are built from the field registry the backend ships in GET /frame-quality/thresholds
// (`fields`), so a new knob appears here automatically with no change to this file.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card, Row, Col, Slider, Switch, Segmented, Input, Button, Tag, Image, Empty, Spin, Alert,
  Typography, Tooltip, Space, Divider, DatePicker,
} from "antd";
import { ExperimentOutlined, ReloadOutlined, TrophyOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getThresholds, simulateSelection } from "../network/api";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
const num2 = (x) => (x == null ? "—" : Number(x).toFixed(2));

const GROUP_LABEL = {
  redaction: "Redaction (ingest)",
  chiller: "Chiller & framing",
  human: "People",
  mask: "Cut-off / mask",
  crop: "Delivered crop",
  ranking: "Ranking",
  sku: "SKU",
};

// One control (slider or switch), rendered from a registry field descriptor.
const FieldControl = ({ f, value, live, onChange }) => {
  const changed = value !== live;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <Text style={{ fontSize: 12, fontWeight: changed ? 600 : 400 }}>
          {f.label}
          {changed && <Tag color="processing" style={{ marginLeft: 6 }}>changed</Tag>}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
          {f.type === "boolean" ? (value ? "on" : "off") : value}
          {changed && <Text type="secondary" style={{ fontSize: 11 }}> (live {f.type === "boolean" ? (live ? "on" : "off") : live})</Text>}
        </Text>
      </div>
      {f.type === "boolean" ? (
        <Switch size="small" checked={!!value} onChange={(v) => onChange(v)} style={{ marginTop: 4 }} />
      ) : (
        <Slider
          min={f.min}
          max={f.max}
          step={f.step || (f.type === "fraction" ? 0.01 : 1)}
          value={Number(value)}
          onChange={(v) => onChange(v)}
          tooltip={{ formatter: (v) => v }}
        />
      )}
      {f.help && <Text type="secondary" style={{ fontSize: 11, display: "block" }}>{f.help}</Text>}
      {f.note && <Text type="warning" style={{ fontSize: 11, display: "block" }}>{f.note}</Text>}
    </div>
  );
};

const verdictTag = (d) => {
  if (d.isWinner) return <Tag color="green" icon={<TrophyOutlined />}>selected</Tag>;
  if (d.simVerdict === "eligible") return <Tag color="blue">would send</Tag>;
  if (d.simVerdict === "blocked") return <Tag color="red">blocked</Tag>;
  return <Tag>{d.simVerdict || "—"}</Tag>;
};

// Did the trial verdict change vs what production recorded for this frame? (best-effort compare)
const changedFromProd = (d) => {
  const simSent = d.isWinner || d.simVerdict === "eligible";
  const prodSent = d.prodSelected || d.prodVerdict === "eligible";
  return d.prodVerdict != null || d.prodSelected ? simSent !== prodSent : false;
};

// One frame in a batch section: thumbnail (full image on preview), the trial verdict + reason,
// its scores, and a Δ flag when the trial verdict differs from what production recorded.
const FrameCard = ({ d }) => {
  const flipped = changedFromProd(d);
  return (
    <div style={{ border: d.isWinner ? "2px solid #52c41a" : "1px solid #303030", borderRadius: 6, padding: 6, height: "100%" }}>
      <Image
        src={d.thumbnailUrl || d.deliveredCropUrl || d.absolutePath}
        preview={{ src: d.deliveredCropUrl || d.absolutePath }}
        alt=""
        loading="lazy"
        style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 4 }}
        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
      />
      <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {verdictTag(d)}
        {flipped && <Tooltip title="Trial verdict differs from what production recorded"><Tag color="gold">Δ</Tag></Tooltip>}
      </div>
      <Text style={{ fontSize: 10, display: "block", marginTop: 2 }} ellipsis={{ tooltip: d.simReason }}>{d.simReason}</Text>
      <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
        chiller {pct(d.chillerConfidence)} · focus {d.focusScore != null ? Math.round(d.focusScore) : "—"}
      </Text>
      <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
        person {num2(d.humanMaxScore)}{d.humanInCrop != null ? ` · crop ${num2(d.humanInCrop)}` : ""} · SKU {d.skuTotal != null ? d.skuTotal : "—"}
      </Text>
      {(d.prodVerdict || d.prodSelected) && (
        <Text type="secondary" style={{ fontSize: 10, display: "block", opacity: 0.7 }}>
          prod: {d.prodSelected ? "selected" : d.prodVerdict}
        </Text>
      )}
    </div>
  );
};

const SelectionTuning = ({ initialDeviceId, initialBatch, cameras = [] }) => {
  const [fields, setFields] = useState([]);
  const [live, setLive] = useState({});
  const [trial, setTrial] = useState({});
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState(null);

  const [source, setSource] = useState(initialBatch ? "batch" : "batch");
  const [batchInput, setBatchInput] = useState(initialBatch || "");
  const [deviceInput, setDeviceInput] = useState(initialDeviceId || "");
  const [range, setRange] = useState([dayjs().subtract(2, "day").startOf("day"), dayjs().endOf("day")]);

  const [batches, setBatches] = useState(null);
  const [notes, setNotes] = useState([]);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState(null);

  // Load the field registry + live values once.
  useEffect(() => {
    setMetaLoading(true);
    getThresholds()
      .then((res) => {
        const fs = Array.isArray(res?.fields) ? res.fields : [];
        setFields(fs);
        setLive(res?.thresholds || {});
        setTrial(res?.thresholds || {});
        setMetaError(null);
      })
      .catch((err) => setMetaError(err?.response?.data?.error || "Could not load thresholds"))
      .finally(() => setMetaLoading(false));
  }, []);

  // The keys whose trial value differs from live — what gets sent as overrides.
  const overrides = useMemo(() => {
    const o = {};
    for (const f of fields) if (trial[f.key] !== live[f.key]) o[f.key] = trial[f.key];
    return o;
  }, [fields, trial, live]);
  const changedCount = Object.keys(overrides).length;

  const runSim = useCallback(
    async (trialValues) => {
      const ov = {};
      for (const f of fields) if (trialValues[f.key] !== live[f.key]) ov[f.key] = trialValues[f.key];
      const body = { thresholds: ov };
      if (source === "batch") {
        const b = (batchInput || "").trim().replace(/^#/, "");
        if (!b) { setBatches(null); return; }
        body.batchId = b;
      } else {
        const d = (deviceInput || "").trim();
        if (!d) { setBatches(null); return; }
        body.deviceId = d;
        body.from = range?.[0]?.startOf("day").toISOString();
        body.to = range?.[1]?.endOf("day").toISOString();
      }
      setSimLoading(true);
      setSimError(null);
      try {
        const res = await simulateSelection(body);
        setBatches(Array.isArray(res?.batches) ? res.batches : []);
        setNotes(Array.isArray(res?.notes) ? res.notes : []);
      } catch (err) {
        setSimError(err?.response?.data?.error || "Simulation failed");
        setBatches([]);
      } finally {
        setSimLoading(false);
      }
    },
    [fields, live, source, batchInput, deviceInput, range]
  );

  const setField = (key, value) => {
    const next = { ...trial, [key]: value };
    setTrial(next);
    runSim(next); // re-verdict live on each committed change
  };

  const resetToLive = () => {
    setTrial(live);
    runSim(live);
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const f of fields) (g[f.group] = g[f.group] || []).push(f);
    return g;
  }, [fields]);

  if (metaLoading) return <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>;
  if (metaError) return <Alert type="error" showIcon message={metaError} />;

  return (
    <Row gutter={16}>
      {/* Left: the knobs */}
      <Col xs={24} lg={9} xl={8}>
        <Card
          size="small"
          title={<span><ExperimentOutlined /> Trial thresholds</span>}
          extra={
            <Space>
              <Tag color={changedCount ? "processing" : "default"}>{changedCount} changed</Tag>
              <Tooltip title="Reset every knob back to the current live production value">
                <Button size="small" icon={<ReloadOutlined />} onClick={resetToLive} disabled={!changedCount}>Reset</Button>
              </Tooltip>
            </Space>
          }
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Testing only"
            description="Drag a value to see how this batch would be judged. Nothing here changes production — the live Threshold Bench remains that path."
          />
          {Object.keys(grouped).map((grp) => (
            <div key={grp} style={{ marginBottom: 8 }}>
              <Divider orientation="left" style={{ margin: "8px 0", fontSize: 12 }}>{GROUP_LABEL[grp] || grp}</Divider>
              {grouped[grp].map((f) => (
                <FieldControl key={f.key} f={f} value={trial[f.key]} live={live[f.key]} onChange={(v) => setField(f.key, v)} />
              ))}
            </div>
          ))}
        </Card>
      </Col>

      {/* Right: the batch/device photos, re-verdicted */}
      <Col xs={24} lg={15} xl={16}>
        <Card
          size="small"
          title="What-if result"
          extra={
            <Space wrap>
              <Segmented
                size="small"
                value={source}
                onChange={(v) => { setSource(v); setBatches(null); }}
                options={[{ label: "By batch", value: "batch" }, { label: "By device", value: "device" }]}
              />
              {source === "batch" ? (
                <Input.Search
                  allowClear placeholder="Batch # (e.g. 8f40c3)" style={{ width: 190 }}
                  value={batchInput} onChange={(e) => setBatchInput(e.target.value)}
                  onSearch={() => runSim(trial)} enterButton
                />
              ) : (
                <>
                  <Input.Search
                    allowClear placeholder="Device ID" style={{ width: 150 }}
                    value={deviceInput} onChange={(e) => setDeviceInput(e.target.value)}
                    onSearch={() => runSim(trial)} enterButton
                  />
                  <RangePicker
                    allowClear={false} value={range} size="small"
                    onChange={(v) => v && setRange(v)}
                  />
                  <Button size="small" onClick={() => runSim(trial)}>Run</Button>
                </>
              )}
            </Space>
          }
        >
          {simError && <Alert type="error" showIcon message={simError} style={{ marginBottom: 12 }} />}
          {notes.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {notes.map((n, i) => (
                <Text key={i} type="secondary" style={{ fontSize: 11, display: "block" }}>• {n}</Text>
              ))}
            </div>
          )}
          <Spin spinning={simLoading}>
            {!batches ? (
              <Empty description={source === "batch" ? "Enter a batch number to replay it." : "Enter a device to replay its recent batches."} />
            ) : batches.length === 0 ? (
              <Empty description="No batches found for that batch/device in this range." />
            ) : (
              batches.map((b) => (
                <div key={b.batchId || "x"} style={{ marginBottom: 18 }}>
                  <Divider orientation="left" style={{ margin: "6px 0 10px", fontSize: 13 }}>
                    <Space wrap size={6}>
                      <Text strong>{b.batchShort ? `#${b.batchShort}` : "batch"}</Text>
                      {b.runAt && <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(b.runAt).format("MM/DD HH:mm")}</Text>}
                      <Text type="secondary" style={{ fontSize: 12 }}>{b.total} frames</Text>
                      {b.selectedNothing
                        ? <Tag color="warning" style={{ marginInlineEnd: 0 }}>selects nothing</Tag>
                        : <Tag color="green" icon={<TrophyOutlined />} style={{ marginInlineEnd: 0 }}>winner</Tag>}
                    </Space>
                  </Divider>
                  <Row gutter={[12, 12]}>
                    {b.data.map((d) => (
                      <Col key={d._id} xs={12} sm={8} md={6}><FrameCard d={d} /></Col>
                    ))}
                  </Row>
                </div>
              ))
            )}
          </Spin>
        </Card>
      </Col>
    </Row>
  );
};

export default SelectionTuning;
